import {
  Alert,
  Button,
  Container,
  Group,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core'
import { AxiosError } from 'axios'
import { TFunction } from 'i18next'
import { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, To, isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { ZodError } from 'zod'
import classes from './ErrorView.module.css'

export function ErrorView({ to }: { to?: To }) {
  const { t } = useTranslation()
  const error = useRouteError()
  const theme = useMantineTheme()
  const isRouteError = isRouteErrorResponse(error)
  const labelStyle = {
    '--error-view-label-color':
      theme.colorScheme === 'dark'
        ? theme.colors.dark[4]
        : theme.colors.gray[2],
  } as CSSProperties

  return (
    <Container
      className={`${classes.root} ${
        isRouteError ? classes.routeErrorRoot : ''
      }`}
    >
      {isRouteError ? (
        <>
          <div className={classes.label} style={labelStyle}>
            {error.status}
          </div>

          <Title className={classes.title}>
            {t('generic.secretPlaceTitle')}
          </Title>
          <Text
            c="dimmed"
            size="lg"
            ta="center"
            className={classes.description}
          >
            {error.status === 404 && t('generic.secretPlaceNote')}
            {error.status !== 404
              ? error.statusText
              : t('systemMessages.unknownErrorOccured')}
          </Text>
        </>
      ) : (
        <ErrorFallbackPure error={error} />
      )}
      {!!to && (
        <Group justify="center">
          <Button component={Link} to={to} size="md">
            {t('generic.takeMeBack')}
          </Button>
        </Group>
      )}
    </Container>
  )
}

const getRouteErrorText = (t: TFunction, status?: number) => {
  switch (status) {
    case 404:
      return t('systemMessages.resourceDoesNotExist')
    case 401:
    case 403:
      return t('systemMessages.noAccessToResource')
    default:
      return t('systemMessages.unableToDetermineError')
  }
}

export const ErrorFallbackPure = ({ error }: { error: unknown }) => {
  const { t } = useTranslation()
  return (
    <Container>
      {error instanceof AxiosError ? (
        <Alert
          title={`${t('systemMessages.anErrorOccured')} (${
            error.response?.status ?? error.status
          } ${error.response?.statusText ?? null})`}
          color={'red'}
          variant="light"
        >
          {getRouteErrorText(t, error.response?.status)}
        </Alert>
      ) : error instanceof ZodError ? (
        <Alert title={t('systemMessages.unexpectedDataStructure')} color="red">
          {error.issues.map(issue => issue.message).join(', ')}
        </Alert>
      ) : error instanceof Error ? (
        <Alert title={t('systemMessages.unknownError')} color="red">
          {error.message}
        </Alert>
      ) : null}
    </Container>
  )
}
